// src/messaging/ConversationView.jsx
// 1つの会話（右の列）【2026-10-16】
//
//   読み込み:
//     ・最初に新しい方から50件。上までスクロールしたら古いものを足す（before_id）
//     ・新しいものは after_id で足す。リアルタイムの合図が来たとき・つながっていないときは4秒ごと
//       （合図が来ていても切れに気づけないことがあるので、20秒ごとにも確かめる）
//     ・直した・取り消した・リアクションの合図はそのメッセージだけ取り直す
//   既読:
//     ・会話を開いていて、窓が前に出ていて、いちばん下まで見えているときだけ、表示したメッセージまで既読にする
//     ・「全部既読にする」ボタンは作らない。先生とのやり取り・グループには既読の表示を出さない（1対1だけ）
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { msgApi } from './api';
import {
  conversationTitle, dayLabel, dateHour, dateTime, dateOnly, featureForKind, isSuspended,
  lsGet, lsSet, realtimeReady, sameDay, useRealtime, useWindowFocus, KIND_LABEL,
} from './util';
import MessageItem from './MessageItem';
import Composer from './Composer';
import NotesPanel from './NotesPanel';
import { SearchPanel, RenamePanel, MembersPanel } from './ConversationPanels';
import { ReportDialog, Toast } from './ui';
import MsgIcon from './MsgIcon';
import Icon from '../components/Icon';
import { confirmDialog } from '../components/ConfirmDialog';

const PAGE = 50;
const NEAR = 80;

function mergeMessages(prev, incoming) {
  if (!incoming || !incoming.length) return prev;
  const map = new Map(prev.map(m => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort((a, b) => a.id - b.id);
}

const NUDGE_TEXT = 'この会話は大学の連絡用です。通報があったときは運営者が内容を確認することがあります。個人的なやり取りは、LINE などの暗号化されたアプリの方が安心です';

function acceptanceNotice(a) {
  if (!a) return null;
  if (a.state === 'off_hours') {
    return `今は先生の受付時間外です。送ったメッセージは${a.next_open_at ? `${dateHour(a.next_open_at)}に` : '次の受付時間に'}先生へ通知されます。`;
  }
  if (a.state === 'paused') {
    const until = a.pause && a.pause.ends_on ? `${dateOnly(a.pause.ends_on)}まで` : 'しばらく';
    return `先生は${until}受付を休んでいます。送ったメッセージは受付が始まってから通知されます。${a.pause && a.pause.note ? `（${a.pause.note}）` : ''}`;
  }
  return null;
}

export default function ConversationView({
  conversationId, user, summary, features, featuresLoaded, suspendedUntil, onBack, onChanged, onOpenConversation,
}) {
  const id = conversationId;
  const meId = user.id;
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState('');
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const [otherReadId, setOtherReadId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [heldOpen, setHeldOpen] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [newBelow, setNewBelow] = useState(false);
  const [flashId, setFlashId] = useState(null);

  const [closedInfo, setClosedInfo] = useState(null);      // { fallback_text } 先生が受け付けていない
  const [sendBlock, setSendBlock] = useState('');          // 送れない理由（サーバーから）
  const [teacherChanged, setTeacherChanged] = useState(false);
  const [acceptance, setAcceptance] = useState(null);       // 学生: 先生の受付状況
  const [offHoursNote, setOffHoursNote] = useState('');
  const [toast, setToast] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState(null);                // search / notes / members / rename
  const [report, setReport] = useState(null);
  const [noteSignal, setNoteSignal] = useState(0);
  const [handled, setHandled] = useState(summary ? summary.handled : undefined);
  const [cgInfo, setCgInfo] = useState(false);

  const listRef = useRef(null);
  const msgsRef = useRef([]);
  const scrollIntent = useRef(null);       // 'bottom' / { keep: 以前の scrollHeight } / { toId }
  const lastMarked = useRef(0);
  const loadingOlder = useRef(false);
  const loadingNewer = useRef(false);
  const atBottomRef = useRef(true);
  const focused = useWindowFocus();
  msgsRef.current = messages;

  useEffect(() => { if (summary && summary.handled !== undefined) setHandled(summary.handled); }, [summary]);

  // ── 読み込み ─────────────────────────────────────────
  const loadDetail = useCallback(async () => {
    const r = await msgApi.get(`/conversations/${id}`);
    if (r.success) {
      setDetail(r.conversation); setDetailErr('');
      setOtherReadId(r.conversation.other_read_id);
      if (!lastMarked.current) lastMarked.current = r.conversation.last_read_id || 0;
    } else setDetailErr(r.message || '会話を開けませんでした');
    return r;
  }, [id]);

  const loadLatest = useCallback(async () => {
    const r = await msgApi.get(`/conversations/${id}/messages?limit=${PAGE}`);
    if (!r.success) { setLoaded(true); return; }
    scrollIntent.current = 'bottom';
    setMessages(r.messages); setHasOlder(r.has_more); setHasNewer(false);
    setOtherReadId(r.other_read_id); setLoaded(true); setNewBelow(false);
  }, [id]);

  const loadOlder = useCallback(async () => {
    const first = msgsRef.current[0];
    if (!first || loadingOlder.current) return;
    loadingOlder.current = true;
    const r = await msgApi.get(`/conversations/${id}/messages?before_id=${first.id}&limit=${PAGE}`);
    loadingOlder.current = false;
    if (!r.success) return;
    scrollIntent.current = { keep: listRef.current ? listRef.current.scrollHeight : 0, top: listRef.current ? listRef.current.scrollTop : 0 };
    setMessages(prev => mergeMessages(prev, r.messages));
    setHasOlder(r.has_more);
  }, [id]);

  /** 新しいもの。jumping（検索で古い所へ飛んだ）ときは1ページずつ */
  const loadNewer = useCallback(async (paging = false) => {
    if (loadingNewer.current) return;
    const list = msgsRef.current;
    const last = list[list.length - 1];
    if (!last) { if (!paging) loadLatest(); return; }
    loadingNewer.current = true;
    try {
      let after = last.id;
      for (let i = 0; i < 5; i++) {
        const r = await msgApi.get(`/conversations/${id}/messages?after_id=${after}&limit=${paging ? PAGE : 100}`);
        if (!r.success) return;
        setOtherReadId(r.other_read_id);
        if (r.messages.length) {
          const othersNew = r.messages.some(m => !m.mine);
          if (atBottomRef.current && !paging) scrollIntent.current = 'bottom';
          else if (othersNew && !paging) setNewBelow(true);
          setMessages(prev => mergeMessages(prev, r.messages));
          after = r.messages[r.messages.length - 1].id;
        }
        if (paging) { setHasNewer(r.has_more); return; }
        if (!r.has_more) return;
      }
    } finally { loadingNewer.current = false; }
  }, [id, loadLatest]);

  /** 1件だけ取り直す（直した・取り消した・リアクション） */
  const refreshOne = useCallback(async (mid) => {
    if (!msgsRef.current.some(m => m.id === mid)) return;
    const r = await msgApi.get(`/conversations/${id}/messages?before_id=${mid + 1}&limit=1`);
    if (r.success && r.messages.length && r.messages[0].id === mid) setMessages(prev => mergeMessages(prev, r.messages));
    else if (r.success) setMessages(prev => prev.filter(m => m.id !== mid)); // 見えなくなった（ブロックなど）
  }, [id]);

  /** 下の方の数十件を取り直す（リアルタイムが無いときに、直した・リアクションを拾うため） */
  const refreshRecent = useCallback(async () => {
    const list = msgsRef.current;
    if (!list.length) return;
    const r = await msgApi.get(`/conversations/${id}/messages?before_id=${list[list.length - 1].id + 1}&limit=30`);
    if (r.success) setMessages(prev => mergeMessages(prev, r.messages));
  }, [id]);

  const jumpTo = useCallback(async (mid) => {
    if (msgsRef.current.some(m => m.id === mid)) {
      scrollIntent.current = { toId: mid };
      setFlashId(mid);
      setMessages(prev => [...prev]); // スクロールを動かすため
      return;
    }
    const [a, b] = await Promise.all([
      msgApi.get(`/conversations/${id}/messages?before_id=${mid + 1}&limit=25`),
      msgApi.get(`/conversations/${id}/messages?after_id=${mid}&limit=25`),
    ]);
    if (!a.success || !b.success) { setToast('そのメッセージを開けませんでした'); return; }
    scrollIntent.current = { toId: mid };
    setFlashId(mid);
    setMessages(mergeMessages(a.messages, b.messages));
    setHasOlder(a.has_more); setHasNewer(b.has_more);
  }, [id]);

  useEffect(() => {
    setDetail(null); setMessages([]); setLoaded(false); lastMarked.current = 0;
    loadDetail(); loadLatest();
  }, [loadDetail, loadLatest]);

  // 学生: 先生とのやり取りでは、先生の受付状況を先に見せる
  useEffect(() => {
    if (!detail || detail.kind !== 'teacher_dm' || detail.my_role !== 'student' || detail.status !== 'active' || !detail.class) return;
    let alive = true;
    msgApi.get(`/classes/${detail.class.id}/contact`).then(r => {
      if (!alive || !r.success) return;
      setAcceptance(r.acceptance);
      if (r.acceptance && r.acceptance.state === 'closed') setClosedInfo({ fallback_text: r.acceptance.fallback_text });
    });
    return () => { alive = false; };
  }, [detail]);

  // 授業グループを初めて開いたときの案内（端末ごとに1回）
  useEffect(() => {
    if (detail && detail.kind === 'class_group' && !lsGet(`msg.cg-info.${detail.id}`)) setCgInfo(true);
  }, [detail]);

  // ── リアルタイム・取り直し ─────────────────────────────────
  useRealtime((ev) => {
    if (Number(ev.conversationId) !== id) return;
    if (ev.type === 'message') { if (ev.change) refreshOne(Number(ev.messageId)); else if (!hasNewer) loadNewer(); }
    else if (ev.type === 'reaction') refreshOne(Number(ev.messageId));
    else if (ev.type === 'read') loadDetail();
    else if (ev.type === 'conversation') { loadDetail(); onChanged && onChanged(); }
    else if (ev.type === 'note') setNoteSignal(n => n + 1);
  });

  useEffect(() => {
    let tick = 0;
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      tick++;
      const rt = realtimeReady();
      if (!hasNewer && (!rt || tick % 5 === 0)) loadNewer();
      if (!rt && tick % 4 === 0) refreshRecent();
      if (tick % 15 === 0) loadDetail();
    }, 4000);
    return () => clearInterval(t);
  }, [hasNewer, loadNewer, refreshRecent, loadDetail]);

  useEffect(() => {
    if (!focused) return;
    if (!hasNewer) loadNewer();
    loadDetail();
  }, [focused]); // eslint-disable-line react-hooks/exhaustive-deps -- 窓が前に出たときだけ

  // ── スクロール ───────────────────────────────────────
  useLayoutEffect(() => {
    const el = listRef.current;
    const intent = scrollIntent.current;
    if (!el || !intent) return;
    scrollIntent.current = null;
    if (intent === 'bottom') el.scrollTop = el.scrollHeight;
    else if (intent.keep !== undefined) el.scrollTop = el.scrollHeight - intent.keep + intent.top;
    else if (intent.toId) {
      const node = el.querySelector(`[data-mid="${intent.toId}"]`);
      if (node) node.scrollIntoView({ block: 'center' });
    }
  }, [messages]);

  useEffect(() => {
    if (!flashId) return undefined;
    const t = setTimeout(() => setFlashId(null), 2000);
    return () => clearTimeout(t);
  }, [flashId]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR;
    atBottomRef.current = bottom;
    if (bottom !== atBottom) setAtBottom(bottom);
    if (bottom) setNewBelow(false);
    if (el.scrollTop < 60 && hasOlder) loadOlder();
    if (bottom && hasNewer) loadNewer(true);
  };

  // ── 既読 ─────────────────────────────────────────────
  const isTeacherSide = !!detail && detail.kind === 'teacher_dm' && detail.my_role === 'teacher';
  const heldList = useMemo(() => (isTeacherSide ? messages.filter(m => m.held) : []), [isTeacherSide, messages]);
  const shown = useMemo(() => (isTeacherSide && !heldOpen ? messages.filter(m => !m.held) : messages), [isTeacherSide, heldOpen, messages]);
  const clearToast = useCallback(() => setToast(''), []);

  useEffect(() => {
    if (!detail || !focused || !atBottom || hasNewer || !shown.length) return;
    const maxId = shown[shown.length - 1].id;
    if (maxId <= lastMarked.current) return;
    lastMarked.current = maxId;
    msgApi.post(`/conversations/${id}/read`, { last_id: maxId }).then(r => { if (r.success && onChanged) onChanged(); });
  }, [detail, focused, atBottom, hasNewer, shown, id, onChanged]);

  // ── 操作 ─────────────────────────────────────────────
  const onSent = (msg, offHours) => {
    scrollIntent.current = 'bottom';
    if (msg) setMessages(prev => mergeMessages(prev, [msg]));
    setReplyTo(null);
    if (offHours) setOffHoursNote(`受付時間外です。先生には${offHours.notify_at ? dateHour(offHours.notify_at) + 'に' : '次の受付時間に'}通知されます`);
    else setOffHoursNote('');
    onChanged && onChanged();
  };

  const onEdited = (msg) => {
    setEditing(null);
    if (msg) setMessages(prev => mergeMessages(prev, [msg]));
  };

  const onFailure = (r) => {
    switch (r.code) {
      case 'TEACHER_CLOSED': setClosedInfo({ fallback_text: r.fallback_text || null }); break;
      case 'TEACHER_CHANGED': setTeacherChanged(true); break;
      case 'NOT_ENROLLED': case 'FEATURE_OFF': case 'NOT_TEACHER': setSendBlock(r.message); break;
      case 'CONVERSATION_STOPPED': case 'LEFT': setSendBlock(r.message); loadDetail(); onChanged && onChanged(); break;
      case 'SUSPENDED': setSendBlock(`メッセージ機能の利用が一時的に停止されています${r.until ? `（${dateTime(r.until)}まで）` : ''}`); break;
      default: break;
    }
  };

  const reopenTeacherDm = async () => {
    if (!detail || !detail.class) return;
    const r = await msgApi.post(`/classes/${detail.class.id}/teacher-dm`);
    if (r.success) { setTeacherChanged(false); onOpenConversation(r.conversation_id); onChanged && onChanged(); }
    else if (r.code === 'TEACHER_CLOSED') { setTeacherChanged(false); setClosedInfo({ fallback_text: r.fallback_text || null }); }
    else setToast(r.message || '開けませんでした');
  };

  const react = async (msg, emoji, isMine) => {
    const r = isMine ? await msgApi.del(`/messages/${msg.id}/reaction`) : await msgApi.put(`/messages/${msg.id}/reaction`, { emoji });
    if (!r.success) { setToast(r.message || 'リアクションできませんでした'); return; }
    refreshOne(msg.id);
  };

  const retract = async (msg) => {
    if (!(await confirmDialog('このメッセージの送信を取り消しますか？\n相手の画面では「メッセージの送信を取り消しました」と表示されます。'))) return;
    const r = await msgApi.post(`/messages/${msg.id}/retract`);
    if (!r.success) { setToast(r.message || '取り消せませんでした'); return; }
    refreshOne(msg.id);
    onChanged && onChanged();
  };

  const block = async (who) => {
    if (!who) return;
    if (!(await confirmDialog(`${who.name}さんをブロックしますか？\n相手には知らされません。相手のメッセージは見えなくなり、1対1のメッセージも届かなくなります。設定の「ブロック中の人」から解除できます。`))) return;
    const r = await msgApi.post('/blocks', { user_id: who.id });
    if (!r.success) { setToast(r.message || 'ブロックできませんでした'); return; }
    setToast(`${who.name}さんをブロックしました`);
    loadLatest(); loadDetail(); onChanged && onChanged();
  };

  const togglePin = async () => {
    setMenuOpen(false);
    const r = detail.pinned ? await msgApi.del(`/conversations/${id}/pin`) : await msgApi.post(`/conversations/${id}/pin`);
    if (!r.success) { setToast(r.message || '変えられませんでした'); return; }
    loadDetail(); onChanged && onChanged();
  };

  const setNotify = async (level) => {
    const r = await msgApi.put(`/conversations/${id}/notify`, { notify: level });
    if (!r.success) { setToast(r.message || '変えられませんでした'); return; }
    setDetail(d => ({ ...d, notify: level }));
    onChanged && onChanged();
  };

  const exportText = async () => {
    setMenuOpen(false);
    const ok = await msgApi.download(`/conversations/${id}/export`, `messages-${id}.txt`).catch(() => false);
    if (!ok) setToast('書き出せませんでした（1日5回までです）');
  };

  const leave = async () => {
    setMenuOpen(false);
    const extra = detail.kind === 'direct' ? '\n1対1の会話は、抜けると終了します（相手も送れなくなります）。'
      : detail.kind === 'class_group' ? '\n授業グループは、あとで開き直すと戻れます。' : '';
    if (!(await confirmDialog(`この会話から抜けますか？${extra}`))) return;
    const r = await msgApi.post(`/conversations/${id}/leave`);
    if (!r.success) { setToast(r.message || '抜けられませんでした'); return; }
    loadDetail(); onChanged && onChanged();
  };

  const vote = async (decision) => {
    const r = await msgApi.post(`/conversations/${id}/renewal`, { decision });
    if (!r.success) { setToast(r.message || '回答できませんでした'); return; }
    loadDetail(); onChanged && onChanged();
  };

  const dismissNudge = async () => {
    setDetail(d => ({ ...d, nudge: null }));
    await msgApi.post(`/conversations/${id}/nudge/dismiss`);
  };

  const toggleHandled = async () => {
    const next = !handled;
    const r = await msgApi.post(`/conversations/${id}/handled`, { handled: next });
    if (!r.success) { setToast(r.message || '変えられませんでした'); return; }
    setHandled(next);
    onChanged && onChanged();
  };

  // ── 表示 ─────────────────────────────────────────────
  const shownWithDays = useMemo(() => {
    const out = [];
    let prev = null;
    for (const m of shown) {
      const d = new Date(m.created_at);
      if (!prev || !sameDay(prev, d)) out.push({ day: true, key: 'd' + m.id, label: dayLabel(m.created_at) });
      out.push(m);
      prev = d;
    }
    return out;
  }, [shown]);

  if (detailErr && !detail) {
    return (
      <div className="msg-view">
        <div className="msg-view-head">
          <button type="button" className="msg-icon-btn msg-back" onClick={onBack} aria-label="一覧へ戻る"><MsgIcon name="back" size={22} /></button>
          <div className="msg-view-title"><h2>メッセージ</h2></div>
        </div>
        <div className="msg-view-empty"><p className="msg-error">{detailErr}</p></div>
      </div>
    );
  }
  if (!detail) return <div className="msg-view"><div className="msg-view-empty">読み込み中...</div></div>;

  const kind = detail.kind;
  const title = conversationTitle(detail.kind === 'teacher_dm' || detail.kind === 'direct'
    ? { ...detail, title: null, other_user: summary && summary.other_user } : detail, meId);
  const featureOn = !featuresLoaded || !!features[featureForKind(kind)] || !!user.isDeveloper;
  let disabledReason = '';
  if (detail.status !== 'active') {
    disabledReason = detail.purge_after
      ? `この会話は停止しています（読み取り専用）。${dateOnly(detail.purge_after)}に消去されます。必要なら「⋯」→「書き出し」で保存できます。`
      : 'この会話は停止しています（読み取り専用）';
  } else if (detail.left) disabledReason = 'この会話から抜けています';
  else if (!featureOn) disabledReason = 'この機能は今は使えません（読むことはできます）';
  else if (isSuspended(suspendedUntil)) disabledReason = `メッセージ機能の利用が一時的に停止されています（${dateTime(suspendedUntil)}まで）`;
  else if (sendBlock) disabledReason = sendBlock;
  else if (closedInfo) disabledReason = 'この先生は今メッセージを受け付けていません';
  const writable = !disabledReason;
  const notesOn = (features.notes || user.isDeveloper) && ['group', 'class_group', 'direct'].includes(kind);
  const subtitle = [
    KIND_LABEL[kind],
    detail.class && kind === 'teacher_dm' ? detail.class.name : null,
    kind === 'group' || kind === 'class_group' ? `${(detail.members || []).filter(m => !m.left).length}人` : null,
  ].filter(Boolean).join('・');

  return (
    <div className="msg-view">
      <div className="msg-view-head">
        <button type="button" className="msg-icon-btn msg-back" onClick={onBack} aria-label="一覧へ戻る"><MsgIcon name="back" size={22} /></button>
        <div className="msg-view-title">
          <h2>{detail.pinned && <MsgIcon name="pin" size={14} className="msg-pin" />}{title}</h2>
          <p>{subtitle}</p>
        </div>
        {isTeacherSide && handled !== undefined && (
          <button type="button" className={'msg-handled-btn' + (handled ? ' done' : '')} onClick={toggleHandled}
                  title="学生には見えません">
            {handled ? '未対応に戻す' : '対応済みにする'}
          </button>
        )}
        <div className="msg-menu-wrap">
          <button type="button" className="msg-icon-btn" onClick={() => setMenuOpen(o => !o)} aria-label="会話のメニュー" aria-expanded={menuOpen}>
            <MsgIcon name="more" size={22} />
          </button>
          {menuOpen && (
            <>
              <div className="msg-menu-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="msg-popover msg-menu msg-conv-menu">
                <button type="button" onClick={togglePin}>{detail.pinned ? 'ピン留めを外す' : 'ピン留めする'}</button>
                {!detail.left && (
                  <div className="msg-menu-group" role="radiogroup" aria-label="通知">
                    <span className="msg-menu-label">通知</span>
                    {[['all', 'すべて'], ...(kind === 'teacher_dm' ? [] : [['mentions', '@されたときだけ']]), ['off', 'オフ']].map(([v, l]) => (
                      <button type="button" key={v} role="radio" aria-checked={detail.notify === v}
                              className={detail.notify === v ? 'checked' : ''} onClick={() => setNotify(v)}>
                        <span className="msg-check">{detail.notify === v ? '✓' : ''}</span>{l}
                      </button>
                    ))}
                  </div>
                )}
                <button type="button" onClick={() => { setMenuOpen(false); setPanel('search'); }}>検索</button>
                {notesOn && <button type="button" onClick={() => { setMenuOpen(false); setPanel('notes'); }}>ノート</button>}
                {(kind === 'group' || kind === 'class_group') && (
                  <button type="button" onClick={() => { setMenuOpen(false); setPanel('members'); }}>メンバー</button>
                )}
                {kind === 'group' && writable && (
                  <button type="button" onClick={() => { setMenuOpen(false); setPanel('rename'); }}>グループ名の変更</button>
                )}
                <button type="button" onClick={exportText}>書き出し（テキストで保存）</button>
                {kind !== 'teacher_dm' && !detail.left && (
                  <button type="button" className="danger" onClick={leave}>抜ける</button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* お知らせの帯 */}
      {detail.renewal && detail.renewal.open && (
        <div className="msg-banner warn">
          <p>この会話は{detail.renewal.stops_at ? dateOnly(detail.renewal.stops_at) : 'まもなく'}に停止します。続けて使いますか？
            {detail.renewal.my_vote != null && <strong>（「{detail.renewal.my_vote ? '延長する' : 'しない'}」と回答済み。変えられます）</strong>}
          </p>
          <div className="msg-banner-actions">
            <button type="button" className="btn-primary" onClick={() => vote(true)} disabled={detail.renewal.my_vote === true}>延長する</button>
            <button type="button" className="btn-secondary" onClick={() => vote(false)} disabled={detail.renewal.my_vote === false}>しない</button>
          </div>
        </div>
      )}
      {detail.nudge === 'move_private' && (
        <div className="msg-banner info">
          <p>{NUDGE_TEXT}</p>
          <button type="button" className="msg-icon-btn sm" onClick={dismissNudge} aria-label="閉じる"><Icon name="x" size={16} /></button>
        </div>
      )}
      {cgInfo && (
        <div className="msg-banner info">
          <p>授業グループの通知は「@されたときだけ」になっています。すべて受け取りたいときは、右上の「⋯」→「通知」から変えられます。この授業の先生は入っていません。</p>
          <button type="button" className="msg-icon-btn sm" aria-label="閉じる"
                  onClick={() => { lsSet(`msg.cg-info.${detail.id}`, '1'); setCgInfo(false); }}><Icon name="x" size={16} /></button>
        </div>
      )}
      {teacherChanged && (
        <div className="msg-banner warn">
          <p>担当の先生が変わりました。新しい先生とのメッセージを開き直してください。</p>
          <div className="msg-banner-actions"><button type="button" className="btn-primary" onClick={reopenTeacherDm}>開き直す</button></div>
        </div>
      )}

      {/* メッセージ */}
      <div className="msg-list" ref={listRef} onScroll={onScroll}>
        {hasOlder && <div className="msg-list-more"><button type="button" className="btn-link" onClick={loadOlder}>前のメッセージを読み込む</button></div>}
        {!loaded && <div className="msg-view-empty">読み込み中...</div>}
        {loaded && !messages.length && <div className="msg-view-empty">まだメッセージはありません</div>}
        {shownWithDays.map(m => (m.day ? <div key={m.key} className="msg-day"><span>{m.label}</span></div> : (
          <MessageItem key={m.id} msg={m} kind={kind} otherReadId={kind === 'direct' ? otherReadId : null}
                       canWrite={writable} highlight={flashId === m.id}
                       onReply={(x) => { setEditing(null); setReplyTo(x); }}
                       onEdit={(x) => { setReplyTo(null); setEditing(x); }}
                       onRetract={retract} onReport={(x) => setReport({ message_id: x.id })} onBlock={block}
                       onReact={react} onJumpTo={jumpTo} onError={setToast} />
        )))}
        {isTeacherSide && heldList.length > 0 && !heldOpen && (
          <button type="button" className="msg-held-fold" onClick={() => { setHeldOpen(true); scrollIntent.current = 'bottom'; }}>
            受付時間外に届いたメッセージ（{heldList.length}件）
            <small>受付時間になると通知されます。押すと表示します</small>
          </button>
        )}
        {hasNewer && <div className="msg-list-more"><button type="button" className="btn-link" onClick={() => loadNewer(true)}>続きを読み込む</button></div>}
      </div>
      {hasNewer && (
        <button type="button" className="msg-jump-latest" onClick={loadLatest}>最新のメッセージへ</button>
      )}
      {!hasNewer && newBelow && (
        <button type="button" className="msg-jump-latest"
                onClick={() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; setNewBelow(false); }}>
          新しいメッセージがあります ↓
        </button>
      )}

      {/* 入力欄 */}
      {offHoursNote && writable && (
        <div className="msg-banner info small"><p>{offHoursNote}</p>
          <button type="button" className="msg-icon-btn sm" onClick={() => setOffHoursNote('')} aria-label="閉じる"><Icon name="x" size={14} /></button>
        </div>
      )}
      {writable && acceptanceNotice(acceptance) && !offHoursNote && (
        <div className="msg-banner info small"><p>{acceptanceNotice(acceptance)}</p></div>
      )}
      {closedInfo && (
        <div className="msg-banner warn">
          <p><strong>この先生は今メッセージを受け付けていません。</strong>
            {closedInfo.fallback_text ? <><br /><span className="msg-prewrap">{closedInfo.fallback_text}</span></> : null}
          </p>
        </div>
      )}
      {writable ? (
        <Composer conversationId={id} members={detail.members} meId={meId}
                  allowMentions={kind !== 'teacher_dm'}
                  templatesEnabled={isTeacherSide}
                  replyTo={replyTo} onCancelReply={() => setReplyTo(null)}
                  editing={editing} onCancelEdit={() => setEditing(null)}
                  onSent={onSent} onEdited={onEdited} onFailure={onFailure} />
      ) : (
        <div className="msg-composer-disabled">{disabledReason}</div>
      )}

      {panel === 'search' && <SearchPanel conversationId={id} onClose={() => setPanel(null)} onJump={jumpTo} />}
      {panel === 'notes' && <NotesPanel conversationId={id} writable={writable} signal={noteSignal} onClose={() => setPanel(null)} />}
      {panel === 'rename' && <RenamePanel conversationId={id} current={detail.title} onClose={() => setPanel(null)}
                                          onDone={() => { loadDetail(); loadNewer(); onChanged && onChanged(); }} />}
      {panel === 'members' && (
        <MembersPanel detail={detail} meId={meId} isStudent={user.role === 'student'}
                      studentChat={!!(features.student_chat || user.isDeveloper)} writable={writable}
                      onClose={() => setPanel(null)} onChanged={() => { loadDetail(); loadNewer(); onChanged && onChanged(); }}
                      onOpenConversation={onOpenConversation} />
      )}
      {report && <ReportDialog target={report} onClose={() => setReport(null)} onDone={setToast} />}
      <Toast text={toast} onClose={clearToast} />
    </div>
  );
}
